// Shared primitives + styled components for the Organization settings page.
// Recreates the design bundle's look (orgsettings.css) using the Curipod
// orange/beige palette. Kept in one module so the General/Members/Students/SSO
// tabs share exact styling.
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';

// ---- Design colors not in the shared theme (from orgsettings.css) ----
export const C = {
  orange: theme.colors.orange,
  orangeDark: theme.colors.orangeDark,
  orangeTint: '#FDE7D1',
  ink: '#282F33',
  fg2: '#575761',
  fg3: 'rgba(40,47,51,.5)',
  grey: '#D9D9D9',
  greyIcon: '#828282',
  navActive: '#F3E8D8',
  hover: '#F6E9DE',
  red: '#E5564B',
  redBg: '#FBE9E7',
  nudgeBg: '#EEEBFA',
  nudgeIcon: '#6B5CC6',
  rowBorder: '#F4ECE0',
  headBorder: '#F0E7DA',
  cardBg: '#fff',
  shadowRow: '0 2px 20px 0 #deaf8d33',
  shadowPop: '0 12px 30px -8px rgba(40,47,51,.18), 0 2px 6px rgba(40,47,51,.08)',
};

// =================== Icons (1.8 stroke, rounded) ===================
const ic = (children: ReactNode, size = 20) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);
export const IconSliders = () =>
  ic(
    <>
      <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M18 18h2" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="16" cy="18" r="2" />
    </>,
  );
export const IconUsers = () =>
  ic(
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c.6-3.2 3-5 6-5s5.4 1.8 6 5" />
      <path d="M16 6.2a3 3 0 0 1 0 5.6" />
      <path d="M21 20c-.4-2.4-1.8-4-4-4.6" />
    </>,
  );
export const IconUser = () =>
  ic(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </>,
  );
export const IconSearch = () =>
  ic(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </>,
  );
export const IconKey = () =>
  ic(
    <>
      <circle cx="8" cy="8" r="4.5" />
      <path d="M11 11l8 8M16 16l2-2M19 19l2-2" />
    </>,
  );
const IconChevDownSm = () => ic(<path d="m6 9 6 6 6-6" />, 16);
export const IconCheck = () => ic(<path d="M20 6 9 17l-5-5" />, 16);
export const IconDots = () =>
  ic(
    <>
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </>,
  );
export const IconPencil = () =>
  ic(
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </>,
    18,
  );
export const IconTrash = () =>
  ic(
    <>
      <path d="M4 7h16" />
      <path d="M10 11v6M14 11v6" />
      <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      <path d="M9 7V4h6v3" />
    </>,
    18,
  );
export const IconPlus = () => ic(<path d="M12 5v14M5 12h14" />, 18);
// =================== Avatar (initials, deterministic color) ===================
const AV_COLORS = [
  '#A1C6DA',
  '#C7BCEB',
  '#B6D9C2',
  '#F0CFC4',
  '#F5DDA0',
  '#E8A8C0',
  '#9DBF9E',
];
export function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}
export function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase();
}
export const Avatar = ({ name, size = 26 }: { name: string; size?: number }) => (
  <AvatarSpan
    style={{
      background: avatarColor(name),
      width: size,
      height: size,
      fontSize: size * 0.42,
    }}
  >
    {initials(name)}
  </AvatarSpan>
);
const AvatarSpan = styled.span`
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  flex-shrink: 0;
  color: #fff;
`;

// =================== Filter dropdown (custom, click-outside) ===================
export type DropdownOption = { value: string; label: string };
export function FilterDropdown({
  value,
  options,
  onChange,
  placeholder = 'All',
  width,
  disabled,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (v: string) => void;
  placeholder?: string;
  width?: number;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const cur = options.find(o => o.value === value);
  const hasValue = value != null && value !== 'all';
  return (
    <DdRoot ref={ref} style={width ? { width } : undefined}>
      <DdTrigger
        type="button"
        disabled={disabled}
        $hasValue={hasValue}
        $open={open}
        onClick={() => setOpen(o => !o)}
        style={width ? { width: '100%', minWidth: 0 } : undefined}
      >
        <DdCur>{cur ? cur.label : placeholder}</DdCur>
        <IconChevDownSm />
      </DdTrigger>
      {open && !disabled && (
        <DdMenu>
          {options.map(o => (
            <DdOpt
              key={o.value}
              type="button"
              $selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <span>{o.label}</span>
              {o.value === value && <IconCheck />}
            </DdOpt>
          ))}
        </DdMenu>
      )}
    </DdRoot>
  );
}

// Multi-select twin of FilterDropdown, sharing its parts so the two read as one
// control. Differences that matter: clicking an option toggles it and leaves the
// menu open (picking several is the normal case), and the trigger summarises the
// selection rather than naming one value.
export function MultiSelectDropdown({
  values,
  options,
  onChange,
  placeholder = 'Select',
  width,
  disabled,
  summarise,
}: {
  values: string[];
  options: DropdownOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  width?: number;
  disabled?: boolean;
  // How to label the trigger when something is selected. Defaults to the labels
  // joined, which is right for short lists; pass a counter for long ones.
  summarise?: (selected: DropdownOption[]) => string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  // Ordered by the option list, not by when each was picked, so the summary reads
  // the same on every visit.
  const selected = options.filter(o => values.includes(o.value));
  const label = selected.length
    ? summarise
      ? summarise(selected)
      : selected.map(o => o.label).join(', ')
    : placeholder;
  return (
    <DdRoot ref={ref} style={width ? { width } : undefined}>
      <DdTrigger
        type="button"
        disabled={disabled}
        $hasValue={selected.length > 0}
        $open={open}
        onClick={() => setOpen(o => !o)}
        style={width ? { width: '100%', minWidth: 0 } : undefined}
      >
        <DdCur>{label}</DdCur>
        <IconChevDownSm />
      </DdTrigger>
      {open && !disabled && (
        <DdMenu>
          {options.map(o => {
            const isOn = values.includes(o.value);
            return (
              <DdOpt
                key={o.value}
                type="button"
                $selected={isOn}
                // Stays open: choosing three subjects shouldn't mean reopening the
                // menu three times.
                onClick={() =>
                  onChange(
                    isOn ? values.filter(v => v !== o.value) : [...values, o.value],
                  )
                }
              >
                <span>{o.label}</span>
                {isOn && <IconCheck />}
              </DdOpt>
            );
          })}
        </DdMenu>
      )}
    </DdRoot>
  );
}

// =================== Kebab row menu (custom, click-outside) ===================
export type RowMenuItem = {
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  onClick: () => void;
};
const ROW_MENU_WIDTH = 176;
export function RowMenu({ items }: { items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false);
  // Portal + fixed positioning so the menu renders on top of the UI instead of
  // being clipped by the table wrapper's overflow:hidden.
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Close on scroll/resize rather than letting the fixed menu detach.
    const onClose = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [open]);

  const toggle = () => {
    if (!open) {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) {
        setCoords({ top: r.bottom + 4, left: Math.max(8, r.right - ROW_MENU_WIDTH) });
      }
    }
    setOpen(o => !o);
  };

  return (
    <RowMenuRoot>
      <IconBtn ref={btnRef} type="button" aria-label="Row actions" onClick={toggle}>
        <IconDots />
      </IconBtn>
      {open &&
        createPortal(
          <RowMenuPop ref={popRef} style={{ top: coords.top, left: coords.left }}>
            {items.map((it, i) => (
              <RowMenuItemBtn
                key={i}
                type="button"
                $danger={!!it.danger}
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
              >
                {it.icon}
                {it.label}
              </RowMenuItemBtn>
            ))}
          </RowMenuPop>,
          document.body,
        )}
    </RowMenuRoot>
  );
}

// =================== Sortable header ===================
export type SortState = { col: string; dir: 'asc' | 'desc' };
export function SortHeader({
  label,
  col,
  sort,
  onSort,
}: {
  label: string;
  col: string;
  sort: SortState;
  onSort: (col: string) => void;
}) {
  const active = sort.col === col;
  return (
    <Th $sortable onClick={() => onSort(col)}>
      <ThInner>
        {label}
        <SortIco $dir={active ? sort.dir : undefined}>
          <span className="up">▲</span>
          <span className="dn">▼</span>
        </SortIco>
      </ThInner>
    </Th>
  );
}

// =================== Styled layout / panel / table ===================
export const Page = styled.div`
  width: 100%;
  padding: 32px 40px 80px;
  padding-left: 280px; /* clear the fixed 240px sidebar */
  background: #fcf4ef;
  min-height: 100vh;

  @media (max-width: 800px) {
    padding: 84px 20px 64px;
  }
`;

export const ContentTitle = styled.h1`
  font-size: 38px;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: ${C.ink};
  margin: 0 0 28px;
`;

export const OsLayout = styled.div`
  display: grid;
  grid-template-columns: 232px 1fr;
  gap: 44px;
  align-items: start;
  max-width: 1180px;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const CatRail = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 3px;
  position: sticky;
  top: 32px;

  @media (max-width: 1080px) {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

export const CatItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border: none;
  background: ${({ $active }) => ($active ? C.navActive : 'transparent')};
  border-radius: 12px;
  cursor: pointer;
  font-size: 17px;
  font-weight: ${({ $active }) => ($active ? 900 : 700)};
  color: ${({ $active }) => ($active ? C.ink : C.fg2)};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? C.navActive : C.hover)};
    color: ${C.ink};
  }
  svg {
    color: ${({ $active }) => ($active ? C.orangeDark : C.greyIcon)};
    flex-shrink: 0;
  }
`;

export const Panel = styled.div`
  min-width: 0;
`;
export const PanelHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
  flex-wrap: wrap;
`;
export const PanelTitle = styled.div`
  font-size: 26px;
  font-weight: 900;
  color: ${C.ink};
  letter-spacing: -0.01em;
`;
export const PanelSub = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${C.fg2};
  margin-top: 4px;
  max-width: 560px;
  line-height: 1.5;
`;

// ---- Settings rows (label + description on the left, control on the right) ----
export const SectionTitle = styled.div`
  font-size: 22px;
  font-weight: 900;
  color: ${C.ink};
  margin: 18px 0 4px;
`;
export const SetRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 22px 0;
  border-bottom: 1px solid ${C.headBorder};
  &:last-child {
    border-bottom: none;
  }
  /* Stack so the control drops under the description on narrow screens. */
  @media (max-width: 992px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;
export const SetRowMain = styled.div`
  flex: 1;
  min-width: 0;
`;
export const SetRowTitle = styled.div`
  font-size: 17px;
  font-weight: 900;
  color: ${C.ink};
`;
export const SetRowDesc = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${C.fg2};
  margin-top: 6px;
  line-height: 1.5;
  max-width: 640px;
`;
export const SetRowControl = styled.div`
  flex-shrink: 0;
  padding-top: 2px;
`;

// ---- Buttons ----
export const BtnOrange = styled.button`
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 800;
  padding: 11px 20px;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
  background: ${C.orange};
  color: #fff;
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    background: ${C.orangeDark};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const BtnGhost = styled.button`
  border: 1.5px solid ${C.grey};
  border-radius: 10px;
  font-size: 15px;
  font-weight: 800;
  padding: 11px 20px;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
  background: #fff;
  color: ${C.ink};
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    border-color: ${C.orange};
    color: ${C.orangeDark};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const BtnDanger = styled.button`
  border: 1.5px solid ${C.red};
  border-radius: 10px;
  font-size: 15px;
  font-weight: 800;
  padding: 11px 20px;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  background: #fff;
  color: ${C.red};
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    background: ${C.red};
    color: #fff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const BtnSm = css`
  padding: 8px 14px;
  font-size: 14px;
  border-radius: 8px;
`;
export const IconBtn = styled.button<{ $danger?: boolean }>`
  background: transparent;
  border: none;
  color: ${C.fg2};
  width: 34px;
  height: 34px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    background: ${({ $danger }) => ($danger ? C.redBg : '#F0E2D5')};
    color: ${({ $danger }) => ($danger ? C.red : C.ink)};
  }
`;

// ---- Toolbar / filters ----
export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;
export const Search = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  background: #fff;
  border: 1.5px solid ${C.grey};
  border-radius: 10px;
  padding: 9px 14px;
  min-width: 280px;
  flex: 1;
  max-width: 360px;
  transition: all 0.2s ease;
  &:focus-within {
    border-color: ${C.orange};
    box-shadow: 0 0 0 3px ${C.orangeTint};
  }
  svg {
    color: ${C.greyIcon};
    flex-shrink: 0;
  }
  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: 15px;
    font-weight: 700;
    background: transparent;
    color: ${C.ink};
    font-family: inherit;
  }
  input::placeholder {
    color: ${C.greyIcon};
    font-weight: 700;
  }
`;
export const ToolbarSpacer = styled.div`
  flex: 1;
`;
export const ResultsCount = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: ${C.fg2};
`;
export const ClearBtn = styled.button`
  ${BtnSm};
  background: #fff;
  border: 1.5px solid ${C.grey};
  color: ${C.ink};
  font-weight: 800;
  cursor: pointer;
  &:hover {
    border-color: ${C.orange};
    color: ${C.orangeDark};
  }
`;

const DdRoot = styled.div`
  position: relative;
`;
const DdTrigger = styled.button<{ $hasValue?: boolean; $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
  min-width: 168px;
  height: 42px;
  padding: 0 14px;
  background: #fff;
  border: 1.5px solid ${({ $hasValue }) => ($hasValue ? C.orange : C.grey)};
  border-radius: 10px;
  font-weight: 800;
  font-size: 15px;
  color: ${({ $hasValue }) => ($hasValue ? C.orangeDark : C.ink)};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    border-color: ${C.orange};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    border-color: ${C.grey};
  }
  svg {
    color: ${C.fg2};
    flex-shrink: 0;
    transition: transform 0.18s;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  }
`;
const DdCur = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const DdMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  background: #fff;
  border: 1px solid ${C.headBorder};
  border-radius: 12px;
  box-shadow: ${C.shadowPop};
  padding: 6px;
  z-index: 40;
  max-height: 320px;
  overflow-y: auto;
`;
const DdOpt = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  background: ${({ $selected }) => ($selected ? C.orangeTint : 'transparent')};
  border: none;
  border-radius: 8px;
  font-weight: ${({ $selected }) => ($selected ? 900 : 700)};
  font-size: 15px;
  color: ${({ $selected }) => ($selected ? C.orangeDark : C.ink)};
  text-align: left;
  cursor: pointer;
  &:hover {
    background: ${({ $selected }) => ($selected ? C.orangeTint : C.navActive)};
  }
  svg {
    color: ${C.orangeDark};
    flex-shrink: 0;
  }
`;

// ---- Table ----
export const TblWrap = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: ${C.shadowRow};
  overflow: hidden;
`;
export const Tbl = styled.table`
  width: 100%;
  border-collapse: collapse;
  thead th {
    text-align: left;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${C.ink};
    padding: 20px 24px;
    border-bottom: 1px solid ${C.headBorder};
    background: #fff;
    /* Labels are two words at most, and uppercase + letter-spacing makes them
       wide enough that "First name" wraps in a narrow column — which then
       drags the whole header row to two lines. */
    white-space: nowrap;
  }
  tbody td {
    padding: 18px 24px;
    border-bottom: 1px solid ${C.rowBorder};
    font-size: 16px;
    font-weight: 700;
    color: ${C.ink};
    vertical-align: middle;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr {
    transition: background 0.15s;
  }
  tbody tr:hover {
    background: #fffbf6;
  }
  .col-actions {
    width: 56px;
    text-align: right;
  }
  .name-cell {
    font-weight: 800;
  }
`;
const Th = styled.th<{ $sortable?: boolean }>`
  ${({ $sortable }) => $sortable && 'cursor: pointer; user-select: none;'}
`;
const ThInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
const SortIco = styled.span<{ $dir?: 'asc' | 'desc' }>`
  display: inline-flex;
  flex-direction: column;
  /* Never let the arrows be squeezed to nothing beside a long label. */
  flex-shrink: 0;
  color: ${C.grey};
  .up,
  .dn {
    font-size: 9px;
    line-height: 7px;
  }
  .up {
    color: ${({ $dir }) => ($dir === 'asc' ? C.orangeDark : C.grey)};
  }
  .dn {
    color: ${({ $dir }) => ($dir === 'desc' ? C.orangeDark : C.grey)};
  }
`;

// ---- Chips / pills ----
export const ChipMuted = styled.span`
  color: ${C.fg3};
  font-weight: 700;
  font-style: italic;
`;
export const IdMono = styled.span`
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 14px;
  font-weight: 600;
  color: ${C.fg2};
  letter-spacing: -0.01em;
`;
// ---- Multi-select chips (assignment) ----
export const SelectChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
export const SelectChip = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid ${({ $on }) => ($on ? C.orange : C.grey)};
  background: ${({ $on }) => ($on ? C.orangeTint : '#fff')};
  color: ${({ $on }) => ($on ? C.orangeDark : C.ink)};
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${C.orange};
  }
`;

// ---- Form fields ----
export const FieldGroup = styled.div`
  margin-bottom: 18px;
`;
export const FieldLabel = styled.label`
  display: block;
  font-size: 15px;
  font-weight: 800;
  color: ${C.ink};
  margin-bottom: 8px;
`;
export const FieldInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid ${C.grey};
  border-radius: 10px;
  background: #fff;
  font-size: 16px;
  font-weight: 700;
  color: ${C.ink};
  font-family: inherit;
  transition: all 0.2s ease;
  &:focus {
    outline: none;
    border-color: ${C.orange};
    box-shadow: 0 0 0 3px ${C.orangeTint};
  }
`;
export const FieldHint = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${C.fg2};
  margin-top: 7px;
`;
export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

// ---- Empty state ----
export const Empty = styled.div`
  text-align: center;
  padding: 56px 20px;
  .empty-title {
    font-size: 19px;
    font-weight: 900;
    color: ${C.ink};
    margin-top: 14px;
  }
  .empty-sub {
    font-size: 15px;
    font-weight: 700;
    color: ${C.fg2};
    margin-top: 6px;
  }
`;

// ---- Members ----
export const RoleBadge = styled.span<{ $admin?: boolean }>`
  display: inline-flex;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  background: ${({ $admin }) => ($admin ? C.nudgeBg : C.orangeTint)};
  color: ${({ $admin }) => ($admin ? C.nudgeIcon : C.orangeDark)};
`;
export const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  .member-name {
    font-weight: 800;
  }
  .member-email {
    font-size: 13px;
    font-weight: 700;
    color: ${C.fg2};
    margin-top: 1px;
  }
`;

const RowMenuRoot = styled.div`
  position: relative;
  display: inline-block;
`;
const RowMenuPop = styled.div`
  position: fixed;
  background: #fff;
  border: 1px solid ${C.headBorder};
  border-radius: 12px;
  box-shadow: ${C.shadowPop};
  padding: 6px;
  z-index: 1000;
  width: ${ROW_MENU_WIDTH}px;
`;
const RowMenuItemBtn = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  color: ${({ $danger }) => ($danger ? C.red : C.ink)};
  text-align: left;
  cursor: pointer;
  &:hover {
    background: ${({ $danger }) => ($danger ? C.redBg : C.navActive)};
  }
  svg {
    color: ${({ $danger }) => ($danger ? C.red : C.fg2)};
    flex-shrink: 0;
  }
`;
